import { FunctionComponent } from 'react';
import { TransactionTypes } from '@/types/document';
import { ApiPaginatedResponse, ApiResponse } from './api';
import { MatchingRecord } from './matching';
import { User } from './user';

export enum BankStatus {
  MANUAL = 'manual',
  LINKED = 'linked',
  EXPIRED = 'expired'
}

export enum BankCardStatus {
  NEEDS_MIGRATION = 'needs_migration',
  EXPIRED = 'expired',
  EXPIRES_SOON = 'expires_soon',
  NEEDS_CONFIRM_INVESTMENT_ACCOUNT = 'needs_confirm_investment_account',
  LINKED = 'linked',
  HAS_INVESTMENTS_EARNINGS = 'has_investments_earnings',
  INVESTMENT_ACCOUNT = 'investment_account',
  MANUAL = 'manual',
  CLOSED = 'closed',
  PENDING_REVIEW = 'pending_review'
}

export type BalanceType =
  | 'CACC'
  | 'CASH'
  | 'CHAR'
  | 'CISH'
  | 'COMM'
  | 'CPAC'
  | 'LLSV'
  | 'LOAN'
  | 'MGLD'
  | 'MOMA'
  | 'NREX'
  | 'ODFT'
  | 'ONDP'
  | 'OTHR'
  | 'SACC'
  | 'SLRY'
  | 'SVGS'
  | 'TAXE'
  | 'TRAN'
  | 'TRAS';

export type Balance = {
  id: string;
  name?: string;
  currency: string;
  account_id: string;
  account_holder: string;
  account_number?: string;
  bank_name: string;
  amount: number;
  iban?: string;
  type: BalanceType;
  /** ERC-20 contract of the asset, when the provider could resolve one. Used for its token logo. */
  contract_address?: string | null;
};

type InvestmentAccount = {
  confirmed: boolean;
  admin: User;
  user: User;
  has_investment_earnings: boolean;
  asked_customer: boolean;
  asked_to_change: boolean;
};

export type BankAccount = {
  _id: string;
  active: boolean;
  enable_automatic_invoice_downloads: boolean;
  name: string;
  balances: Balance[];
  bank_type: string;
  bank_classification?: string;
  refresh_key: string;
  api_enabled: boolean;
  api_enabled_until?: string;
  company: string;
  created: string;
  __v: number;
  status: BankStatus;
  investment_account?: InvestmentAccount;
  crypto_wallet_address?: string;
  crypto_wallet_review?: {
    status: string;
    conflicting_company?: string;
    conflicting_company_name?: string;
    documents?: string[];
  };
  currency?: string;
  last_status_information_request_date?: string;
  erc20_contract_address?: string;
  erc20_token_symbol?: string;
  erc20_token_name?: string;
};

export enum BankConnectionType {
  OPEN_BANKING = 'open_banking', // GoCardless Flow
  API = 'api',
  CRYPTO = 'crypto_wallet',
  ERC20 = 'erc20_wallet',
  MANUAL = 'manual',
  LINK = 'link',
  IBRIT = 'ibrit', // Interactive Brokers IBRIT Web Service
  EXCHANGE_API = 'exchange_api' // crypto exchange API key + secret (e.g. Kraken)
}

export interface ERC20TokenListItem {
  symbol: string;
  name: string;
  contractAddress: string;
  decimals: number;
  logoURI: string | null;
  popularity: number | null;
}

export interface ERC20Token {
  contract: string;
  symbol: string;
  name: string;
  decimals: number;
  balance: string;
  balanceFormatted: number;
  transfers: number;
  type: string;
}

export enum AccountHolderMismatchReason {
  ACCOUNT_HOLDER_MISMATCH_REASON_COMPANY_LEGAL_NAME = 'is_other_company_legal_name', // Account holder ≠ company legal name
  ACCOUNT_HOLDER_MISMATCH_REASON_PERSONAL_NAME = 'is_personal_name' // Account holder = personal name (representative)
}

export interface BankProvider {
  id: string;
  bankType: string;
  description: string;
  logo?: FunctionComponent<any> | string;
  country?: string;
  connectionType: BankConnectionType;
  isPopular?: boolean;
  bankName?: string;
  referralLink?: string;
  isOther?: boolean;
  isRecommended?: boolean;
}

export type BankMigration = Record<string, string>;

export interface BankBalance {
  value: string;
  currency: string;
  account: string;
  created?: string;
  amount: number;
  date?: string;
  description?: string;
  bank?: BankAccount;
  account_id?: string;
  iban?: string;
  source?: string;
  type?: string;
}

export type GetBankAccountsResponse = ApiResponse<{
  banks: BankAccount[];
}>;

export type GetBankMigrationsResponse = ApiResponse<{
  migrations: BankMigration;
}>;

export type GetBanksByCountryResponse = ApiResponse<{
  banks: {
    bic: string;
    countries: string[];
    id: string;
    logo: string;
    name: string;
    transaction_total_days: string;
  }[];
}>;

export type GetAuthorizationLinkForAPIBankConnectionResponse = ApiResponse<{
  link: string;
}>;

export enum AccountingRequestsType {
  REQUEST_INVOICE = 'request_invoice',
  REQUEST_INFO = 'request_info',
  REQUEST_FIX_INVOICE = 'request_fix_invoice',
  NOTIFY_PERSONAL_EXPENSE = 'notify_personal_expense',
  REJECT_DOCUMENT = 'reject_document'
}

export enum AccountingRequestsStatus {
  PENDING = 'pending',
  COMPLETED = 'completed'
}

export type AccountingRequests = {
  _id: string;
  admin_user: string;
  date: string;
  extra_info: string;
  request_type: AccountingRequestsType;
  status: AccountingRequestsStatus;
};

export interface Transaction {
  _id: string;
  transaction_identifier: string;
  account: {
    id: string;
    currency: string;
    iban: string;
    account_id: string;
    api_id: string;
    account_type: string;
    description: string;
  };
  amount: {
    value: number;
    currency: string;
  };
  balance: null | {
    value: number;
    currency: string;
  };
  bank: BankAccount;
  company: string;
  created: string;
  date: string;
  description: string;
  fees: {
    value: number;
    currency: string;
  };
  net: {
    value: number;
    currency: string;
  };
  origin: string;
  recipient: string;
  reference: string;
  status: string;
  tags: string[];
  type: TransactionTypes;
  token_type?: 'native' | 'ERC-20' | null;
  contract_address?: string | null;
  matching?: MatchingRecord;
  accounting_requests?: AccountingRequests[];
  upload_or_match_option?: string;
  isDisabled?: boolean;
}

export type GetTransactionsPaginatedRequest = {
  bankId: string;
  page: number;
  limit: number;
  filters?: Object;
  year: number;
  month: number;
  search?: string;
  sort?: string;
};

export type ExportTransactionsRequest = {
  bankId: string;
  format: string;
  year: number;
  month: number;
  filter?: Object;
};

export type GetBalancesPaginatedRequest = {
  bankId: string;
  filters?: Object;
  year: number;
  month: number;
  search?: string;
};

export type GetTransactionsPaginatedResponse = ApiPaginatedResponse<{
  transactions: Transaction[];
}>;

export type GetBalancesPaginatedResponse = ApiPaginatedResponse<{
  balances: BankBalance[];
}>;

export type GetTransactionsByDateRangePaginatedRequest = {
  bankId: string | null;
  page: number;
  limit: number;
  filters?: Object;
  startDate?: Date | null;
  endDate?: Date | null;
  search?: string;
  sortBy?: string | null;
};

export type GetTransactionsByDateRangePaginatedResponse = ApiPaginatedResponse<{
  transactions: Transaction[];
}>;

export enum TransactionStatus {
  PENDING = 'pending',
  BOOKED = 'booked'
}

export interface GetBankAccountsRequest {
  showDisabled: boolean;
  filters?: Object;
}

export interface UploadBankStatementRequest {
  bankId: string;
  filedata: string;
  filename: string;
}

export interface DeleteBankRequest {
  bankId: string;
}

export type GetBankTypesResponse = ApiResponse<{
  bank_types: {
    open_banking_banks: string[];
    api_banks: string[];
    custom_banks: string[];
  };
}>;

export interface GetAuthorizationLinkForAPIBankConnectionRequest {
  bankId: string;
  bankName: string;
  isMigration: boolean;
  isReconnection: boolean;
  bankToReconnect?: string | null;
}

export interface SetBankAccountInvestmentPropertyRequest {
  bankId: string;
  isInvestment: boolean;
}

export interface AskToChangeBankAccountInvestmentPropertyRequest {
  bankId: string;
  isInvestment: boolean;
}

export type BankResponse = ApiResponse<{ bank: BankAccount }>;

export interface CreatePayoneerRegistrationLinkRequest {
  createNew?: boolean;
  bankName: string;
  reconnection?: boolean;
}

export interface ConnectKrakenRequest {
  apiKey: string;
  apiSecret: string;
  bankName: string;
}

export interface BankAccountOption {
  label: string;
  value: string;
}

export interface NotifyAccountHolderMismatchRequest {
  account_holder: string;
  company_legal_name: string;
  bank_name: string;
  mismatch_reason: AccountHolderMismatchReason;
}

export enum BankStatusAccountantRequestAction {
  ACTIVE_TRANSACTIONS = 'active_transactions',
  ACTIVE_NO_TRANSACTIONS = 'active_no_transactions',
  CLOSED = 'closed'
}

export interface SendAccountantRequestResponseAndActionRequest {
  bankId: string;
  action: BankStatusAccountantRequestAction;
}
